import React from 'react';
import { Box, Grid, Text } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';

const MotionBox = motion(Box);

const SPECS = [
  { term: 'Height',             detail: '1,650 to 1,800 mm',         note: 'configurable'              },
  { term: 'Mass',               detail: '72 to 95 kg',                note: 'configuration dependent'   },
  { term: 'Payload',            detail: 'Up to 40 kg',                note: 'balanced'                  },
  { term: 'Battery Runtime',    detail: '4 to 8 hours',               note: 'hot-swap capable'           },
  { term: 'Module Interface',   detail: 'Universal O-ID Bus',         note: 'power · data · coolant'    },
  { term: 'Degrees of Freedom', detail: '34 to 52 DOF',               note: 'configuration dependent'   },
  { term: 'IP Rating',          detail: 'IP54 standard',              note: 'IP67 with hazmat modules'  },
  { term: 'Compute',            detail: 'Edge AI, 120 TOPS',          note: 'field-upgradeable'         },
  { term: 'Connectivity',       detail: '5G / Wi-Fi 7 / Mesh radio',  note: null                        },
  { term: 'Module Swap Time',   detail: '< 8 min per module',         note: 'no special tooling'        },
];

function Specs() {
  const shouldReduce = useReducedMotion();
  const ease = [0.25, 0.1, 0.25, 1];

  return (
    <Box as="section" id="specs" aria-label="Platform specifications"
      bg="oid.bgTint" py={{ base: '55px', md: '89px' }}>
      <Grid templateColumns={{ base: '1fr', md: '38.2% 1fr' }}
        gap={{ base: 12, md: 16 }} alignItems="start"
        maxWidth="1600px" mx="auto" px={{ base: 6, lg: 12 }}>

        {/* Left — 38.2% */}
        <MotionBox
          initial={shouldReduce ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease }}
          position={{ md: 'sticky' }}
          top={{ md: 'calc(var(--nav-h) + 32px)' }}>
          <Box mb={6}>
            <Box as="span" display="block" width="24px" height="2px"
              bg="oid.accent" mb={4} aria-hidden="true" />
            <Text fontSize="11px" fontWeight={600} letterSpacing="0.3em"
              textTransform="uppercase" color="oid.accent">07. Specifications</Text>
          </Box>
          <Text as="h2" fontWeight={700} lineHeight={1.1} color="oid.text"
            sx={{ fontSize: 'clamp(28px, 3vw, 44px)' }} mb={6}>
            Platform<br/>Details
          </Text>
          <Text fontSize="0.9375rem" fontWeight={400} color="oid.muted"
            lineHeight={1.618} maxWidth="260px">
            All specifications refer to the O-ID Series I Field Edition.
            Configuration-dependent values reflect the full module range.
          </Text>
        </MotionBox>

        {/* Right — spec table */}
        <Box as="dl" m={0} aria-label="O-ID platform specifications">
          {SPECS.map((spec, i) => (
            <MotionBox key={spec.term}
              initial={shouldReduce ? false : { opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: i * 0.045, ease }}
              display="grid"
              gridTemplateColumns={{ base: '1fr', sm: '38.2% 1fr' }}
              gap={{ base: 1, sm: 6 }}
              py={5}
              borderBottom="1px solid rgba(22,33,58,0.1)"
              _last={{ borderBottom: 'none' }}>
              <Text as="dt" fontSize="xs" fontWeight={500} letterSpacing="0.12em"
                textTransform="uppercase" color="rgba(22,33,58,0.45)"
                alignSelf="baseline" pt={{ base: 0, sm: '2px' }}>
                {spec.term}
              </Text>
              <Box as="dd" m={0}>
                <Text fontSize="0.9375rem" fontWeight={600} color="oid.text" lineHeight={1.5}>
                  {spec.detail}
                </Text>
                {spec.note && (
                  <Text fontSize="xs" fontWeight={400} color="rgba(22,33,58,0.45)"
                    mt={1} lineHeight={1.5}>{spec.note}</Text>
                )}
              </Box>
            </MotionBox>
          ))}
        </Box>
      </Grid>
    </Box>
  );
}
export default Specs;
